import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  ConfirmDialogComponent,
  ConfirmDialogModel,
  defaultDialogActions,
  DialogResponse,
} from '@app/shared/components/confirm-dialog/confirm-dialog.component';
import {
  VisitSelectionDialogComponent,
  VisitSelectionDialogData,
} from '@app/shared/components/visit-selection-dialog/visit-selection-dialog.component';
import * as moment from 'moment';
import { defer, EMPTY, Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { PatientEventVoStatus } from './api-client.service';
import { ApplicationInsightsService, AppInsightTags } from './application-insights.service';
import { PatientInWaitingRoomViewModel } from './state/provider/provider.store';
import { UiStateStore } from './state/ui-state/ui-state.store';

type ConsultVisitPromptVariant =
  | 'earlier-visit'
  | 'multiple-visits'
  | 'booking-today'
  | 'multiple-bookings'
  | 'unfinished-with-visit'
  | 'unfinished-with-booking';
type ConsultVisitPromptChoice = 'continue' | 'today' | 'cancel';
type UnfinishedConsultChoice = 'continue' | 'today';

export interface NovaVisitGroups {
  inProgress?: PatientInWaitingRoomViewModel;
  unfinished?: PatientInWaitingRoomViewModel;
  today: PatientInWaitingRoomViewModel[];
  earlier: PatientInWaitingRoomViewModel[];
}

export function groupNovaVisits(visits: PatientInWaitingRoomViewModel[], now: Date = new Date()): NovaVisitGroups {
  const isToday = (visit: PatientInWaitingRoomViewModel) => moment(visit.PatientEvent.CheckInTime).isSame(now, 'day');
  const started = visits.filter(visit => visit.PatientEvent.Status === PatientEventVoStatus._2);
  const checkedIn = visits.filter(visit => visit.PatientEvent.Status !== PatientEventVoStatus._2);
  return {
    inProgress: started.find(isToday),
    unfinished: started.find(visit => !isToday(visit)),
    today: checkedIn.filter(isToday),
    earlier: checkedIn.filter(visit => !isToday(visit)),
  };
}

const unfinishedConsultChoices: { [key: string]: UnfinishedConsultChoice } = {
  [DialogResponse.CONTINUE]: 'continue',
  [DialogResponse.NEW]: 'today',
};

const formatDay = (date: Date) => moment(date).format(moment(date).isSame(moment(), 'year') ? 'DD MMM' : 'DD MMM YYYY');
const formatTime = (date: Date) => moment(date).format('HH:mm');
// styles.scss: roomier buttons for these prompts
const panelClass = 'consult-visit-prompt';

// Consult-start prompts for Nova practices. Every prompt completes empty on cancel and never
// offers to create a new visit: a visit for this patient already exists in each of these situations.
@Injectable({ providedIn: 'root' })
export class ConsultVisitResolverService {
  constructor(
    private dialog: MatDialog,
    private appInsightsService: ApplicationInsightsService,
    private uiStateStore: UiStateStore
  ) {}

  resolveEarlierVisit$(visits: PatientInWaitingRoomViewModel[]): Observable<PatientInWaitingRoomViewModel> {
    if (visits.length === 1) {
      const visit = visits[0];
      const day = formatDay(visit.PatientEvent.CheckInTime);
      return this.confirm$('earlier-visit', visits.length, {
        title: `Continue the visit from ${day}?`,
        message:
          `The only open visit for this patient was checked in on <b>${day} at ${formatTime(visit.PatientEvent.CheckInTime)}</b>. ` +
          `A consult started now saves under ${day}, and <b>its invoice is dated ${day}</b>. ` +
          `To record today instead, cancel and ask reception to close the ${day} visit and check the patient in for today.`,
        actions: [defaultDialogActions.CANCEL, { key: DialogResponse.CONTINUE, value: 'Continue', color: 'primary' }],
      }).pipe(map(() => visit));
    }

    const days = visits.map(visit => `${formatDay(visit.PatientEvent.CheckInTime)} ${formatTime(visit.PatientEvent.CheckInTime)}`);
    return this.select$('multiple-visits', visits.length, {
      title: `This patient has ${visits.length} open visits`,
      message:
        `Visits from <b>${days.join('</b> and <b>')}</b> are still checked in. To record today's consult, <b>cancel and ask reception</b> ` +
        `to check out the older visits and check the patient in for today, then start the consult. ` +
        `Continue only if you are completing one of those older consults.`,
      options: visits.map((visit, index) => ({
        label: `Continue the visit from ${days[index]}`,
        description: `Checked in ${moment(visit.PatientEvent.CheckInTime).fromNow()}`,
        value: visit,
      })),
      confirmLabel: 'Continue selected visit',
    });
  }

  resolveBooking$(bookings: PatientInWaitingRoomViewModel[]): Observable<PatientInWaitingRoomViewModel> {
    if (bookings.length === 1) {
      const booking = bookings[0];
      const time = formatTime(booking.PatientEvent.CheckInTime);
      const consequence = booking.PatientEvent.Patient?.PatientXRef
        ? `The consult will attach to it, <b>be dated today</b>, and the booking will be marked as checked in on the Nova calendar. ` +
          `If this consult is not for that booking, cancel - a separate visit is booked and checked in by reception.`
        : `The consult will attach to it and <b>be dated today</b>. <b>Reception still needs to check the patient in</b> on the Nova calendar, ` +
          `because the account has not been created there yet.`;
      return this.confirm$('booking-today', bookings.length, {
        title: `Use today's booking?`,
        message: `This patient has a booking <b>today at ${time}</b> that has not been checked in yet. ${consequence}`,
        actions: [defaultDialogActions.CANCEL, { key: DialogResponse.CONTINUE, value: 'Start consult', color: 'primary' }],
      }).pipe(map(() => booking));
    }

    return this.select$('multiple-bookings', bookings.length, {
      title: `Use one of today's bookings?`,
      message:
        `This patient has <b>${bookings.length} bookings today</b> that have not been checked in yet. ` +
        `Choose the booking this consult is for, or cancel and ask reception to check the patient in.`,
      options: bookings.map(booking => ({
        label: `Booking at ${formatTime(booking.PatientEvent.CheckInTime)}`,
        description: booking.PatientEvent.ProviderNote,
        value: booking,
      })),
      confirmLabel: 'Start consult',
    });
  }

  // Asked only when a visit or booking today competes with an earlier day's unfinished consult;
  // with nothing today the doctor is finishing that consult and it resumes without a prompt.
  resolveUnfinishedConsult$(
    unfinished: PatientInWaitingRoomViewModel,
    todayCandidates: PatientInWaitingRoomViewModel[],
    booked: boolean
  ): Observable<UnfinishedConsultChoice> {
    const day = formatDay(unfinished.PatientEvent.CheckInTime);
    const time = formatTime(todayCandidates[0].PatientEvent.CheckInTime);
    const todayVisit = !booked
      ? `was checked in <b>today at ${time}</b>`
      : todayCandidates.length === 1
        ? `has a booking <b>today at ${time}</b>`
        : `has <b>${todayCandidates.length} bookings today</b>`;
    const variant: ConsultVisitPromptVariant = booked ? 'unfinished-with-booking' : 'unfinished-with-visit';
    const data: ConfirmDialogModel = {
      title: `Continue the unfinished consult from ${day}?`,
      message:
        `This patient ${todayVisit}, and a consult from <b>${day}</b> was never finished. ` +
        `Continuing saves it under ${day}, and <b>its invoice is dated ${day}</b>. ` +
        `<b>Start today's consult</b> to record it under today; the ${day} consult stays open to finish later.`,
      actions: [
        defaultDialogActions.CANCEL,
        { key: DialogResponse.CONTINUE, value: `Continue ${day} consult`, color: 'basic' },
        { key: DialogResponse.NEW, value: `Start today's consult`, color: 'primary' },
      ],
    };
    return this.afterClosed$(() => this.dialog.open(ConfirmDialogComponent, { width: '600px', panelClass, data })).pipe(
      map(result => unfinishedConsultChoices[result?.key]),
      tap(choice => this.track(variant, todayCandidates.length, choice ?? 'cancel')),
      switchMap(choice => (choice ? of(choice) : EMPTY))
    );
  }

  private confirm$(variant: ConsultVisitPromptVariant, candidates: number, data: ConfirmDialogModel): Observable<boolean> {
    return this.afterClosed$(() => this.dialog.open(ConfirmDialogComponent, { width: '480px', panelClass, data })).pipe(
      map(result => result?.key === DialogResponse.CONTINUE),
      tap(continued => this.track(variant, candidates, continued ? 'continue' : 'cancel')),
      switchMap(continued => (continued ? of(true) : EMPTY))
    );
  }

  private select$<T>(variant: ConsultVisitPromptVariant, candidates: number, data: VisitSelectionDialogData<T>): Observable<T> {
    return this.afterClosed$<T>(() =>
      this.dialog.open(VisitSelectionDialogComponent, { width: '480px', disableClose: true, panelClass, data })
    ).pipe(
      tap((selected: T) => this.track(variant, candidates, selected != null ? 'continue' : 'cancel')),
      switchMap((selected: T) => (selected != null ? of(selected) : EMPTY))
    );
  }

  // Consult start runs under the app's progress spinner, which is drawn above dialogs: hide it while the prompt is open.
  // The store, not UiStateService: that service imports ClinicalEncounterService, which imports this one.
  private afterClosed$<R>(open: () => MatDialogRef<unknown, R>): Observable<R> {
    return defer(() => {
      const spinnerShown = this.uiStateStore.getValue().displayProgressSpinner;
      if (spinnerShown) {
        this.uiStateStore.update({ displayProgressSpinner: false });
      }
      return open()
        .afterClosed()
        .pipe(tap(() => spinnerShown && this.uiStateStore.update({ displayProgressSpinner: true })));
    });
  }

  private track(variant: ConsultVisitPromptVariant, candidates: number, choice: ConsultVisitPromptChoice) {
    this.appInsightsService.trackEvent(AppInsightTags.CONSULT_START_VISIT_PROMPT, {
      Variant: variant,
      Choice: choice,
      Candidates: String(candidates),
    });
  }
}
